import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  ButtonV2,
  DatePicker,
  LoaderPending,
  ModalV2,
  dsV2,
  FieldV2,
  CheckRow,
  InfoV2,
} from '@bloomays-lib/ui.shared';
import { AvatarCell, avatarToneFromSeed } from '@bloomays-lib/ui.shared/datagrid';
import { FileRejection } from 'react-dropzone';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_LEGAL_CONTRACTS_BY_MISSION,
  GET_LEGAL_CONTACT_MISSION,
  UPLOAD_AMENDMENT,
} from '@bloomays-lib/adapter.api-bloomer';
import { notify } from '../../helpers/toastify';
import {
  BrowserUploadFile,
  LegalContractGQL,
  UploadAmendmentGQLInput,
  UploadAmendmentGQLResult,
  GetLegalContactMissionParams,
  GetLegalContactMissionResponse,
  TemplateContract,
  IContact,
} from '@bloomays-lib/types.shared';
import {
  getLegalContactSourceI18nKey,
  initialsFromName,
  isAmendmentStep,
  isMainContractStep,
} from '@bloomays-lib/utils.shared';
import { Upload } from './Upload';
import isEmpty from 'lodash/fp/isEmpty';
import { Upload as UploadGQL } from 'graphql-upload';
import { format } from 'date-fns/fp/format';
import { Logger } from '../../services/serviceLogger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';
import ContractTypeBadge from './ContractTypeBadge';

const logger = Logger('LegalContractFile');

const disabledButton = (signedDate: Date | null, files: BrowserUploadFile[], signed: boolean) => {
  if (signed) {
    if (!signedDate) return true;
  }
  if (isEmpty(files)) return true;
  return false;
};

const contactInitials = (contact: IContact): string => {
  const fromNames = `${contact.firstname?.[0] ?? ''}${contact.lastname?.[0] ?? ''}`.toUpperCase();
  return fromNames || initialsFromName(contact.fullname ?? '');
};

const contactDisplayName = (contact: IContact): string =>
  contact.fullname || `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim() || '—';

type UploadButtonProps = {
  legalContract: LegalContractGQL;
  signed: boolean;
  missionRecordId: string;
  setUploadingFile: (value: boolean) => void;
  onClickPreview: (file: string) => void;
  /** Hide the default paperclip/preview trigger (opened from a parent menu). */
  hideTrigger?: boolean;
  /** Controlled open state for the upload modal. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const LegalContractFile = ({
  legalContract,
  signed,
  missionRecordId,
  onClickPreview,
  setUploadingFile,
  hideTrigger = false,
  open,
  onOpenChange,
}: UploadButtonProps) => {
  const { t } = useTranslation(['legalContracts', 'mission', 'extendShortenMission', 'random']);
  const [uploadAmendment, { loading: uploadingFile, error: errorUpload }] = useMutation<UploadAmendmentGQLResult>(
    UPLOAD_AMENDMENT,
    {
      refetchQueries: [
        {
          query: GET_LEGAL_CONTRACTS_BY_MISSION,
          variables: {
            missionId: missionRecordId,
          },
        },
        'getLegalContractsWithSummary',
      ],
    },
  );
  const { recordId: legalContractRecordId, step } = legalContract;
  const contractFileUrl = signed ? legalContract.signedFileUrl : legalContract.emptyFileUrl;
  const [openPopin, setOpenPopin] = useState(false);
  const [createInvitation, setCreateInvitation] = useState(false);
  const [signedDate, setSignedDate] = useState<Date | null>(null);
  const [files, setFiles] = useState<BrowserUploadFile[]>([]);
  const canRequestSignature = useMemo(() => isAmendmentStep(step) || isMainContractStep(step), [step]);

  useEffect(() => {
    if (open !== undefined) {
      setOpenPopin(open);
    }
  }, [open]);

  const closePopin = () => {
    setOpenPopin(false);
    setFiles([]);
    setCreateInvitation(false);
    setSignedDate(null);
    onOpenChange?.(false);
  };

  const openUploadPopin = () => {
    setOpenPopin(true);
    onOpenChange?.(true);
  };

  const shouldFetchMissionLegalContacts = openPopin && !signed && canRequestSignature;

  const { data: missionData, loading: fetchingMissionLegalContacts } = useQuery<
    GetLegalContactMissionResponse,
    GetLegalContactMissionParams
  >(GET_LEGAL_CONTACT_MISSION, {
    variables: {
      recordId: missionRecordId,
    },
    skip: !shouldFetchMissionLegalContacts,
    fetchPolicy: 'network-only',
  });

  const mission = missionData?.missions?.[0];
  const resolvedClientPdsContact = mission?.resolvedClientPdsContact ?? null;
  const resolvedSupplierSstContact = mission?.resolvedSupplierSstContact ?? null;
  const resolvedSignerForContract =
    legalContract.type === TemplateContract.PDS ? resolvedClientPdsContact : resolvedSupplierSstContact;
  const signersContact = resolvedSignerForContract?.contact ?? null;
  const signersSource = resolvedSignerForContract?.source ?? null;

  const isSignerUsable = !!signersContact?.email;
  const shouldDisableCreateInvitation = fetchingMissionLegalContacts || !isSignerUsable;
  const contract = legalContract.step.includes('Avenant') ? t('amendmant') : t('mainContract');
  const uploadFileTitle = t('uploadFile');
  const couldBeSigned = legalContract.status !== 'done';
  const typeLabel = t(`voletType.${legalContract.type}`);
  const typeBadgeColors =
    legalContract.type === TemplateContract.SST
      ? { color: dsV2.color.bleu, backgroundColor: dsV2.color.bleuDoux }
      : { color: '#B42318', backgroundColor: dsV2.color.rougeLight };
  const signerRoleLabel =
    legalContract.type === TemplateContract.PDS
      ? t('defaultPdsSigner', { ns: 'extendShortenMission' })
      : t('defaultSstSigner', { ns: 'extendShortenMission' });
  const sourceLabel =
    signersContact && signersSource ? t(getLegalContactSourceI18nKey(signersSource), { ns: 'mission' }) : undefined;

  const upload = async () => {
    const input: UploadAmendmentGQLInput = {
      file: files[0] as UploadGQL,
      legalContractRecordId,
      signed,
      missionRecordId,
      createInvitation,
      signedDate: signedDate !== null ? format('yyyy-MM-dd', signedDate) : undefined,
    };
    const { data } = await uploadAmendment({
      variables: {
        input,
      },
    });
    if (data) {
      notify('success', 'Fichier uploadé avec succès.');
      closePopin();
    }
  };

  useEffect(() => {
    if (errorUpload) {
      notify('error', `⛔️ ERROR ${errorUpload}`);
      logger.error('Error uploading amendment:', { legalContractRecordId, step }, errorUpload);
      monitorException(errorUpload, { legalContractRecordId, step });
    }
  }, [errorUpload, legalContractRecordId, step]);

  useEffect(() => {
    setUploadingFile(uploadingFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingFile]);

  useEffect(() => {
    if (!shouldFetchMissionLegalContacts) return;
    if (shouldDisableCreateInvitation && createInvitation) setCreateInvitation(false);
  }, [shouldDisableCreateInvitation, createInvitation, shouldFetchMissionLegalContacts]);

  if (!contractFileUrl) {
    return (
      <>
        <ModalV2
          open={openPopin}
          onClose={closePopin}
          width={560}
          iconTone="violet"
          icon={<CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />}
          title={<ModalTitle>{t('uploadDocumentTitle')}</ModalTitle>}
          footer={
            <FooterRow>
              {!signed && canRequestSignature ? (
                <YousignHint>
                  <YousignMark aria-hidden>Y</YousignMark>
                  <span>
                    {t('signatureViaYousignPrefix')} <strong>{t('signatureViaYousignBrand')}</strong>
                  </span>
                </YousignHint>
              ) : (
                <span />
              )}
              <FooterActions>
                <ButtonV2 variant="secondary" onClick={closePopin}>
                  {t('cancel', { ns: 'random' })}
                </ButtonV2>
                <ButtonV2
                  variant="primary"
                  loading={uploadingFile}
                  disabled={disabledButton(signedDate, files, signed)}
                  startIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={async () => {
                    await upload();
                  }}
                >
                  {uploadFileTitle}
                </ButtonV2>
              </FooterActions>
            </FooterRow>
          }
        >
          <HeaderSubtitle>
            {contract}
            <SubtitleDot aria-hidden>·</SubtitleDot>
            <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
          </HeaderSubtitle>

          {canRequestSignature && !signed && couldBeSigned ? (
            <InfoBanner>
              <WarningAmberRoundedIcon sx={{ fontSize: 16, color: dsV2.color.violet, flexShrink: 0, mt: '1px' }} />
              <span>{t('uploadDocumentInfo')}</span>
            </InfoBanner>
          ) : null}

          {signed ? (
            <FieldV2 label={t('signedDate')}>
              <DatePicker
                label={t('signedDate')}
                handleChange={(e: Date | null) => e !== null && setSignedDate(e)}
                value={signedDate}
              />
            </FieldV2>
          ) : null}

          <FieldV2 label={t('documentSectionLabel')} hint={t('documentUploadHint')}>
            <DropzoneShell>
              <Upload
                id={`input-${legalContractRecordId}`}
                placeholder={contract}
                textLabel={t('uploadDocumentTitle')}
                name={`input-${legalContractRecordId}`}
                fileTypes={['.pdf']}
                maxFiles={1}
                maxLength={5000000}
                displayIcons={true}
                handleChange={(acceptedFiles: BrowserUploadFile[], rejectFiles: FileRejection[]) => {
                  if (rejectFiles.length > 0) {
                    const errors = rejectFiles
                      .map((rf: FileRejection) => {
                        return rf.errors.map((e) => e.message).join(', ');
                      })
                      .join(',');
                    notify('error', `Error uploading some files : ${errors}`);
                    return;
                  }
                  setFiles(acceptedFiles);
                }}
                autoload={true}
                noFeedback={true}
                showFiles={false}
                preloadedFiles={files}
                noModal={true}
              />
            </DropzoneShell>
          </FieldV2>

          {canRequestSignature && !signed && couldBeSigned ? (
            <FieldV2 label={t('signerSectionLabel')}>
              <SignerTypeRow>
                <ContractTypeBadge {...typeBadgeColors}>{typeLabel}</ContractTypeBadge>
                <SignerRoleText>{signerRoleLabel}</SignerRoleText>
              </SignerTypeRow>

              {fetchingMissionLegalContacts ? (
                <LoaderPending text={t('fetchingMissionLegalContacts')} />
              ) : (
                <SignerCard>
                  {signersContact ? (
                    <AvatarCell
                      initials={contactInitials(signersContact)}
                      name={contactDisplayName(signersContact)}
                      tone={avatarToneFromSeed(contactDisplayName(signersContact))}
                      subtitle={[signersContact.nameJob, sourceLabel, signersContact.email].filter(Boolean).join(' · ')}
                    />
                  ) : (
                    <UnavailableText>{t('notAvailable', { ns: 'mission' })}</UnavailableText>
                  )}
                </SignerCard>
              )}

              <CheckRow
                checked={createInvitation}
                disabled={shouldDisableCreateInvitation}
                onChange={setCreateInvitation}
              >
                {t('createInvitation')}
              </CheckRow>

              {shouldDisableCreateInvitation && !fetchingMissionLegalContacts ? (
                <InfoV2 tone="blue" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
                  <ManualAlertTitle>{t('signatureElectroniqueUnavailableTitle')}</ManualAlertTitle>
                  {t('signatureElectroniqueUnavailableDescription')}
                </InfoV2>
              ) : null}

              {createInvitation ? (
                <InfoV2 tone="amber" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
                  <ManualAlertTitle>{t('manualAmendmentAlertTitle')}</ManualAlertTitle>
                  <span dangerouslySetInnerHTML={{ __html: t('manualAmendmentAlertDescription') }} />
                </InfoV2>
              ) : null}
            </FieldV2>
          ) : null}
        </ModalV2>
        {hideTrigger ? null : (
          <label htmlFor="file-input">
            <IconButton size="small" title={uploadFileTitle} onClick={openUploadPopin}>
              <span role="img" aria-label="Icone pour attacher fichier">
                📎
              </span>
            </IconButton>
          </label>
        )}
      </>
    );
  }

  if (hideTrigger) {
    return null;
  }

  return (
    <ActionButton
      variant="contained"
      color="secondary"
      actionType={'Preview'}
      onClick={() => onClickPreview(contractFileUrl)}
      title={contractFileUrl}
    />
  );
};

export default LegalContractFile;

const ModalTitle = styled('span')({
  display: 'block',
  fontFamily: dsV2.font.sansSemiBold,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: dsV2.color.noir,
  lineHeight: 1.3,
});

const HeaderSubtitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  fontFamily: dsV2.font.sans,
  fontSize: 12,
  fontWeight: 500,
  color: dsV2.color.t3,
  lineHeight: 1.3,
  marginBottom: 14,
});

const SubtitleDot = styled('span')({
  color: dsV2.color.t4,
});

const InfoBanner = styled(Box)({
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  padding: '10px 12px',
  marginBottom: 14,
  borderRadius: dsV2.radius.sm,
  fontSize: 12,
  lineHeight: 1.5,
  background: dsV2.color.violetLight,
  color: dsV2.color.violet,
  border: `1px solid ${dsV2.color.violetDim}`,
});

const DropzoneShell = styled(Box)({
  '& > div > div > div:first-of-type': {
    borderWidth: '1.5px !important',
    borderStyle: 'dashed !important',
    borderRadius: `${dsV2.radius.md} !important`,
    borderColor: `${dsV2.color.border} !important`,
    backgroundColor: `${dsV2.color.bg} !important`,
    padding: '20px !important',
  },
});

const SignerTypeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 10,
});

const SignerRoleText = styled('span')({
  fontSize: 12.5,
  fontWeight: 600,
  color: dsV2.color.t2,
});

const SignerCard = styled(Box)({
  border: `1.5px solid ${dsV2.color.bleuBrd}`,
  background: dsV2.color.blanc,
  borderRadius: dsV2.radius.md,
  padding: '12px 14px',
  marginBottom: 14,
  '& > div > div:first-of-type': {
    backgroundColor: `${dsV2.color.bleuDoux} !important`,
    border: `1.5px solid ${dsV2.color.bleuBrd}`,
    boxShadow: dsV2.shadow.sm,
  },
});

const UnavailableText = styled('span')({
  fontSize: 12,
  color: dsV2.color.t4,
});

const ManualAlertTitle = styled('strong')({
  display: 'block',
  marginBottom: 4,
  fontWeight: 600,
});

const FooterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
});

const FooterActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginLeft: 'auto',
});

const YousignHint = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  color: dsV2.color.t3,
  minWidth: 0,
});

const YousignMark = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 4,
  backgroundColor: '#0A2540',
  color: dsV2.color.blanc,
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0,
});
